import { useCallback, useMemo, useState } from 'react'

import type { ResourceSetMember } from '@/features/prequalification/localControl/types'

export type UseResourceBasketResult = {
  /** The working set, in insertion order. */
  resources: ResourceSetMember[]
  /** The working set's ids, for membership checks (e.g. the add panel). */
  selectedIds: Set<string>
  /** Add a set of resources at once; already-present ids are kept as-is. */
  addResources: (toAdd: ResourceSetMember[]) => void
  /** Toggle a single resource's membership. */
  toggleResource: (resource: ResourceSetMember) => void
  /** Drop a single resource from the working set. */
  removeResource: (resourceId: string) => void
  /** Replace the whole working set (e.g. seed an editor from a loaded resourceSet). */
  reset: (next: ResourceSetMember[]) => void
}

/**
 * The resource-picker's working set as a `Map<id, resource>` — shared by the
 * resource set builder (create) and the resource editor (edit). Callers layer their own
 * concerns (edit's seed + diff) on top; this hook owns only membership.
 */
export function useResourceBasket(initial: ResourceSetMember[] = []): UseResourceBasketResult {
  const [basket, setBasket] = useState<Map<string, ResourceSetMember>>(
    () => new Map(initial.map((resource) => [resource.resourceId, resource])),
  )

  const resources = useMemo(() => Array.from(basket.values()), [basket])
  const selectedIds = useMemo(() => new Set(basket.keys()), [basket])

  const addResources = useCallback((toAdd: ResourceSetMember[]) => {
    setBasket((current) => {
      const next = new Map(current)
      for (const resource of toAdd) next.set(resource.resourceId, resource)
      return next
    })
  }, [])

  const toggleResource = useCallback((resource: ResourceSetMember) => {
    setBasket((current) => {
      const next = new Map(current)
      if (next.has(resource.resourceId)) next.delete(resource.resourceId)
      else next.set(resource.resourceId, resource)
      return next
    })
  }, [])

  const removeResource = useCallback((resourceId: string) => {
    setBasket((current) => {
      const next = new Map(current)
      next.delete(resourceId)
      return next
    })
  }, [])

  const reset = useCallback((next: ResourceSetMember[]) => {
    setBasket(new Map(next.map((resource) => [resource.resourceId, resource])))
  }, [])

  return { resources, selectedIds, addResources, toggleResource, removeResource, reset }
}
